import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, Pencil, Check, X } from "lucide-react";
import { api, media, fmtDate } from "@/lib/api";
import { usePaged, Pager, SearchBar } from "@/components/fo/Pager";

const FILTERS = [["pending", "To review"], ["approved", "Approved"], ["draft", "Incomplete"], ["rejected", "Rejected"], ["", "All"]];
const Filter = ({ v, set }) => (
  <div className="mb-5 flex flex-wrap gap-2">
    {FILTERS.map(([k, l]) => <button key={k} onClick={() => set(k)} data-testid={`admin-filter-${k || "all"}`} className={`rounded-full border px-4 py-1.5 font-mono text-[11px] uppercase ${v === k ? "border-[#FF2E63] bg-[#FF2E63]" : "border-white/15 text-slate-400"}`}>{l}</button>)}
  </div>
);

export const ArtistsAdmin = () => {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const pg = usePaged(list, { query: q, fields: ["stage_name", "email", "city", "genre", "role_type"], key: status });
  const load = () => api.get("/admin/artists", { params: { status } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const act = async (id, s) => { await api.post(`/admin/artists/${id}/status`, { status: s }); toast.success(`Artist ${s}`); load(); };
  return (
    <div data-testid="admin-artists-list">
      <Filter v={status} set={setStatus} />
      <SearchBar value={q} set={setQ} placeholder="Search name, email, city, genre…" id="artists" />
      <div className="space-y-3">
        {pg.rows.map((a) => (
          <div key={a.id} className="panel flex flex-wrap items-start gap-5 p-5" data-testid={`admin-artist-${a.id}`}>
            <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-[#07090E]">{a.photo && <img src={media(a.photo)} alt="" className="h-full w-full object-cover" />}</div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg font-bold">{a.stage_name} <span className="font-mono text-xs text-slate-500">{a.email}</span></div>
              <div className="font-mono text-xs uppercase text-slate-500">{a.role_type || "?"} · {a.genre || "?"} · {a.city || "?"} · {a.tracks} tracks · {a.effective_plan}{a.founding ? " · founding" : ""} · {fmtDate(a.created_at)}</div>
              <p className="mt-2 line-clamp-2 text-sm text-slate-400">{a.bio || "No bio yet"}</p>
            </div>
            <div className="flex gap-2">
              {a.status !== "approved" && <button className="btn-hot !px-4 !py-2 text-xs" onClick={() => act(a.id, "approved")} data-testid={`approve-artist-${a.id}`}>Approve</button>}
              {a.status !== "rejected" && <button className="btn-ghost !px-4 !py-2 text-xs" onClick={() => act(a.id, "rejected")} data-testid={`reject-artist-${a.id}`}>Reject</button>}
            </div>
          </div>
        ))}
        {!pg.total && <p className="text-slate-500">Nothing here.</p>}
      </div>
      <Pager {...pg} id="artists" />
    </div>
  );
};

const Title = ({ t, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(t.title);
  const save = async () => {
    try { await api.put(`/admin/tracks/${t.id}/title`, { title: v }); toast.success("Title updated"); setEdit(false); onSaved(); }
    catch (er) { toast.error(er.response?.data?.detail || "Could not save"); }
  };
  if (!edit) return (
    <div className="flex items-center gap-2 font-display font-bold">{t.title}
      <button onClick={() => { setV(t.title); setEdit(true); }} className="text-slate-500 transition-colors hover:text-[#00F5D4]" data-testid={`edit-track-title-${t.id}`}><Pencil className="h-3.5 w-3.5" /></button>
    </div>
  );
  return (
    <div className="flex items-center gap-2">
      <input autoFocus value={v} maxLength={120} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEdit(false); }}
        className="h-9 min-w-0 flex-1 rounded-md border border-white/15 bg-[#07090E] px-3 text-sm" data-testid={`track-title-input-${t.id}`} />
      <button onClick={save} className="text-[#00F5D4]" data-testid={`save-track-title-${t.id}`}><Check className="h-4 w-4" /></button>
      <button onClick={() => setEdit(false)} className="text-slate-500" data-testid={`cancel-track-title-${t.id}`}><X className="h-4 w-4" /></button>
    </div>
  );
};

export const TracksAdmin = () => {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [q, setQ] = useState("");
  const pg = usePaged(list, { query: q, fields: ["title", "artist_name", "genre"], key: status });
  const audio = useRef(null);
  const load = () => api.get("/admin/tracks", { params: { status } }).then((r) => setList(r.data));
  useEffect(() => { load(); return () => audio.current?.pause(); }, [status]); // eslint-disable-line
  const act = async (id, s) => { await api.post(`/admin/tracks/${id}/status`, { status: s }); toast.success(`Track ${s}`); load(); };
  const listen = (t) => {
    if (!audio.current) audio.current = new Audio();
    if (playing === t.id) { audio.current.pause(); return setPlaying(null); }
    audio.current.src = media(t.audio); audio.current.play(); setPlaying(t.id);
  };
  return (
    <div data-testid="admin-tracks-list">
      <Filter v={status} set={setStatus} />
      <SearchBar value={q} set={setQ} placeholder="Search track title or artist…" id="tracks" />
      <div className="space-y-3">
        {pg.rows.map((t) => (
          <div key={t.id} className="panel flex flex-wrap items-center gap-4 p-4" data-testid={`admin-track-${t.id}`}>
            <button onClick={() => listen(t)} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#00F5D4]/50 text-[#00F5D4]" data-testid={`admin-track-play-${t.id}`}>{playing === t.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
            <div className="min-w-0 flex-1"><Title t={t} onSaved={load} /><div className="font-mono text-xs uppercase text-slate-500">{t.artist_name} ({t.artist_status}) · {t.genre} · {t.status}</div></div>
            {t.status !== "approved" && <button className="btn-hot !px-4 !py-2 text-xs" onClick={() => act(t.id, "approved")} data-testid={`approve-track-${t.id}`}>Approve</button>}
            {t.status !== "rejected" && <button className="btn-ghost !px-4 !py-2 text-xs" onClick={() => act(t.id, "rejected")} data-testid={`reject-track-${t.id}`}>Reject</button>}
          </div>
        ))}
        {!pg.total && <p className="text-slate-500">Nothing here.</p>}
      </div>
      <Pager {...pg} id="tracks" />
    </div>
  );
};
